import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";
import { aboutInfo } from "../staticData";
import { 
  Users, 
  Award, 
  Target, 
  Heart,
  ArrowRight,
  CheckCircle,
  Star,
  Shield
} from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="About Us - WOW AUTOBODY | Your Trusted Auto Body Shop Since 2010"
        description="Learn about WOW AUTOBODY's 14+ years of excellence in auto body repair. Meet our ASE certified technicians and discover our commitment to quality and customer satisfaction."
        keywords="about WOW AUTOBODY, auto body shop history, certified auto technicians, trusted auto repair, auto body shop team"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-red-600 text-white mb-6 px-4 py-2">
            About WOW AUTOBODY
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Our Story</h1>
          <p className="text-xl leading-relaxed">
            Proudly serving the community with exceptional auto body repair services 
            since 2010. Your satisfaction is our commitment.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Who We Are</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {aboutInfo.story}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our team of certified technicians combines years of experience with the latest 
                technology to deliver repairs that exceed industry standards. We understand that 
                your vehicle is more than transportation – it's your freedom, your connection to 
                what matters most.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700">ASE Certified Technicians</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700">State-of-the-Art Equipment</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-gray-700">Lifetime Warranty</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1632823469850-1b2f78c0ad28?w=600&h=400&fit=crop" 
                alt="Auto body shop interior" 
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-lg shadow-xl">
                <div className="text-3xl font-bold">14+</div>
                <div className="text-sm">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Mission & Values</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything we do is guided by our commitment to excellence and customer satisfaction.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center border-2 hover:border-blue-200 hover:shadow-lg transition-all">
              <CardContent className="p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="h-8 w-8 text-blue-900" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="p-8 text-center border-2 hover:border-red-200 hover:shadow-lg transition-all">
              <CardContent className="p-0">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Values</h3>
                <p className="text-gray-600 leading-relaxed">
                  Integrity, quality, and customer service are at the heart of everything we do. 
                  We believe in honest communication and transparent pricing.
                </p>
              </CardContent>
            </Card>

            <Card className="p-8 text-center border-2 hover:border-blue-200 hover:shadow-lg transition-all">
              <CardContent className="p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award className="h-8 w-8 text-blue-900" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Promise</h3>
                <p className="text-gray-600 leading-relaxed">
                  Every repair comes with our lifetime warranty and satisfaction guarantee. 
                  We stand behind our work with confidence and pride.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Track Record</h2>
            <p className="text-xl opacity-90">Numbers that speak to our commitment and expertise</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {aboutInfo.stats.map((stat, index) => (
              <div key={index} className="text-center p-6">
                <div className="text-5xl font-bold text-red-400 mb-4">{stat.value}</div>
                <div className="text-lg text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Customers Choose Us</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See what sets WOW AUTOBODY apart from other auto body shops in the area.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Shield,
                title: "Lifetime Warranty",
                description: "All repairs backed by our comprehensive warranty program"
              },
              {
                icon: Users,
                title: "Certified Experts",
                description: "ASE certified technicians with 15+ years of experience"
              },
              {
                icon: Star,
                title: "Premium Quality",
                description: "OEM parts and premium materials for lasting results"
              },
              {
                icon: Award,
                title: "Insurance Approved",
                description: "Trusted by all major insurance companies"
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="p-6 text-center border-2 hover:border-blue-200 hover:shadow-lg transition-all">
                  <CardContent className="p-0">
                    <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-8 w-8 text-blue-900" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600">
              Skilled professionals dedicated to restoring your vehicle to perfection
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Mike Johnson",
                role: "Master Technician & Owner",
                experience: "20+ years",
                image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop"
              },
              {
                name: "Sarah Davis",
                role: "Paint Specialist",
                experience: "12+ years", 
                image: "https://images.unsplash.com/photo-1494790108755-2616b2e16aaa?w=300&h=300&fit=crop"
              },
              {
                name: "Carlos Rodriguez",
                role: "Collision Repair Expert",
                experience: "15+ years",
                image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop"
              }
            ].map((member, index) => (
              <Card key={index} className="overflow-hidden border-2 hover:border-blue-200 hover:shadow-lg transition-all">
                <CardContent className="p-0">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-red-600 font-semibold mb-1">{member.role}</p>
                    <p className="text-gray-600">{member.experience} experience</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-6">Experience the WOW Difference</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of satisfied customers who trust us with their vehicle repairs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold">
                Get Free Estimate
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold">
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;